import * as React from 'react';
import * as SeparatorPrimitive from '@radix-ui/react-separator';

import { cn } from '../../lib/utils';

/**
 * How loud the rule is.
 *
 * The same prop name and the same two words `RuledList` and `IconCard` use, so a
 * reader who knows one knows this. Not called a tone: `Tone` in this repo means
 * danger / warning / success / neutral, and a second sense of the word is the
 * drift `lib/tone.ts` exists to prevent.
 *
 * `muted` is `--hairline`, the line every ordinary rule in the product is drawn
 * with, and it is the default — nothing that already renders a Separator moves.
 *
 * `brand` is `--brand-edge`, mint 700. That is the Tier 2 role for a rule
 * specifically: the bright mint is 1.4:1 on the page and would be a rule nobody
 * can see, and `--brand-text` is the step for words rather than lines. At 4.0:1
 * this clears the 3:1 WCAG asks of a non-text indicator.
 *
 * Tier 2 roles rather than the shadcn contract's `--border`, which is what this
 * file used to draw. The two resolve to the same value today, so the default is
 * unchanged pixel for pixel; the difference is that a lookup we author belongs
 * on the roles, and `--border` has no brand-coloured twin to pair with anyway.
 */
const ACCENT = {
  muted: 'bg-hairline',
  brand: 'bg-brand-edge',
} as const;

function Separator({
  className,
  orientation = 'horizontal',
  decorative = true,
  accent = 'muted',
  ...props
}: React.ComponentProps<typeof SeparatorPrimitive.Root> & {
  /** How loud the rule is. Grey by default; `brand` draws it in the mint. */
  accent?: keyof typeof ACCENT;
}) {
  return (
    <SeparatorPrimitive.Root
      data-slot="separator"
      decorative={decorative}
      orientation={orientation}
      className={cn(
        'shrink-0 data-[orientation=horizontal]:h-px data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-px',
        ACCENT[accent],
        className,
      )}
      {...props}
    />
  );
}

export { Separator };
